import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { SystemBrandConfig } from '@/types/personalization'
import { getPersonalizationConfig } from '@/api/personalization'
import { resolveFilePreviewUrl } from '@/composables/useFileUpload'
import {
  applyFavicon,
  DEFAULT_FAVICON,
  DEFAULT_TITLE_SUFFIX,
  isPublicTenantFaviconLocked
} from '@/composables/useSystemBrandApply'

export const DEFAULT_SYSTEM_BRAND_CONFIG: SystemBrandConfig = {
  systemLogoFile: '',
  systemIconFile: '',
  systemTitle: '',
  controlTowerAssistantEnabled: 1
}

export const useSystemBrandStore = defineStore(
  'systemBrand',
  () => {
    const systemLogoFile = ref(DEFAULT_SYSTEM_BRAND_CONFIG.systemLogoFile)
    const systemIconFile = ref(DEFAULT_SYSTEM_BRAND_CONFIG.systemIconFile)
    const systemTitle = ref(DEFAULT_SYSTEM_BRAND_CONFIG.systemTitle)
    const controlTowerAssistantEnabled = ref<0 | 1>(
      DEFAULT_SYSTEM_BRAND_CONFIG.controlTowerAssistantEnabled
    )

    /** 鉴权拉取后的可展示地址（blob:），供侧栏 / favicon 使用 */
    const logoDisplayUrl = ref('')
    const iconDisplayUrl = ref('')

    let resolveSeq = 0
    const resolveDisplayUrls = async () => {
      if (isPublicTenantFaviconLocked()) return
      const seq = ++resolveSeq
      const logoId = systemLogoFile.value
      const iconId = systemIconFile.value

      const [logoUrl, iconUrl] = await Promise.all([
        logoId ? resolveFilePreviewUrl(logoId) : Promise.resolve(''),
        iconId ? resolveFilePreviewUrl(iconId) : Promise.resolve('')
      ])

      // 避免快速连续更新时旧请求回写
      if (seq !== resolveSeq) return

      // 异步解析期间登录页可能已经应用租户图标，不能再覆盖
      if (isPublicTenantFaviconLocked()) return

      logoDisplayUrl.value = logoUrl
      iconDisplayUrl.value = iconUrl
      applyFavicon(iconDisplayUrl.value || DEFAULT_FAVICON)
    }

    watch([systemLogoFile, systemIconFile], () => {
      void resolveDisplayUrls()
    })

    const effectiveTitle = computed(() => {
      if (systemTitle.value.trim()) {
        return systemTitle.value.trim()
      }
      return DEFAULT_TITLE_SUFFIX
    })

    const effectiveLogoUrl = computed(() => logoDisplayUrl.value)

    const effectiveIconUrl = computed(() => iconDisplayUrl.value || DEFAULT_FAVICON)

    /** M14；兼容 boolean / 字符串持久化 */
    const showAssistant = computed(() => Number(controlTowerAssistantEnabled.value) === 1)

    const getConfig = (): SystemBrandConfig => ({
      systemLogoFile: systemLogoFile.value,
      systemIconFile: systemIconFile.value,
      systemTitle: systemTitle.value,
      controlTowerAssistantEnabled: controlTowerAssistantEnabled.value
    })

    const applySystemBrand = () => {
      if (isPublicTenantFaviconLocked()) return
      applyFavicon(effectiveIconUrl.value)
      void resolveDisplayUrls()
    }

    const updateConfig = (config: Partial<SystemBrandConfig>) => {
      if (config.systemLogoFile !== undefined) {
        systemLogoFile.value = config.systemLogoFile
      }
      if (config.systemIconFile !== undefined) {
        systemIconFile.value = config.systemIconFile
      }
      if (config.systemTitle !== undefined) {
        systemTitle.value = config.systemTitle
      }
      if (config.controlTowerAssistantEnabled !== undefined) {
        controlTowerAssistantEnabled.value = config.controlTowerAssistantEnabled
      }
      // fileId 变化由 watch 触发 resolve；仅标题/助手变更时也刷新一次 favicon
      if (config.systemLogoFile === undefined && config.systemIconFile === undefined) {
        if (!isPublicTenantFaviconLocked()) {
          applyFavicon(effectiveIconUrl.value)
        }
      }
    }

    const resetToDefault = () => {
      updateConfig({ ...DEFAULT_SYSTEM_BRAND_CONFIG })
    }

    const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

    /** 防止并发重复拉取（登录瞬间 + 进入主布局 + 回到前台可能同时触发） */
    let hydrating = false

    /**
     * 从 enterprise-config/current 拉取 M13/M14 并生效。
     *
     * 无痕 / 全新浏览器没有本地缓存（ct_system_brand_config），首次拉取失败就只剩默认 logo，
     * 所以这里对「请求异常」和「返回空」都做有限次重试——登录瞬间企业上下文可能尚未就绪。
     * 成功时覆盖本地缓存；全部失败时沿用本地缓存，不影响已展示内容。
     */
    const hydrateFromApi = async (options: { retries?: number } = {}) => {
      if (hydrating) return
      hydrating = true

      const retries = options.retries ?? 2
      try {
        for (let attempt = 0; attempt <= retries; attempt++) {
          try {
            const response = await getPersonalizationConfig()
            if (!response) {
              // 接口正常但无配置：已有本地缓存时直接沿用；完全没有时才重试（首次登录场景）
              const hasLocal = !!(systemLogoFile.value || systemIconFile.value || systemTitle.value)
              if (!hasLocal && attempt < retries) {
                await sleep(300 * (attempt + 1))
                continue
              }
              console.warn('[CT_SYSTEM_BRAND] 接口未返回系统品牌配置，沿用本地缓存')
              return
            }

            const config = (response as any).content || response
            const toFlag = (value: unknown, defaultValue: 0 | 1 = 1): 0 | 1 => {
              if (value === 0 || value === '0' || value === false) return 0
              if (value === 1 || value === '1' || value === true) return 1
              return defaultValue
            }
            updateConfig({
              systemLogoFile: config.systemLogoFile || '',
              systemIconFile: config.systemIconFile || '',
              systemTitle: config.systemTitle || '',
              controlTowerAssistantEnabled: toFlag(config.controlTowerAssistantEnabled, 1)
            })
            await resolveDisplayUrls()
            return
          } catch (error) {
            if (attempt < retries) {
              await sleep(300 * (attempt + 1))
              continue
            }
            console.warn('[CT_SYSTEM_BRAND] 从接口加载系统品牌配置失败，沿用本地缓存', error)
          }
        }
      } finally {
        hydrating = false
      }
    }

    return {
      systemLogoFile,
      systemIconFile,
      systemTitle,
      controlTowerAssistantEnabled,
      effectiveTitle,
      effectiveLogoUrl,
      effectiveIconUrl,
      showAssistant,
      getConfig,
      updateConfig,
      resetToDefault,
      applySystemBrand,
      hydrateFromApi
    }
  },
  {
    persist: {
      storage: localStorage,
      key: 'ct_system_brand_config',
      paths: [
        'systemLogoFile',
        'systemIconFile',
        'systemTitle',
        'controlTowerAssistantEnabled'
      ]
    }
  }
)

/** 组装页面标题：`页面名 - 系统标题`（供 router guards 等使用） */
export function buildDocumentTitle(pageTitle: string): string {
  try {
    const store = useSystemBrandStore()
    const suffix = store.effectiveTitle || DEFAULT_TITLE_SUFFIX
    if (!pageTitle) {
      return suffix
    }
    return `${pageTitle} - ${suffix}`
  } catch {
    return pageTitle ? `${pageTitle} - ${DEFAULT_TITLE_SUFFIX}` : DEFAULT_TITLE_SUFFIX
  }
}
